import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight, Plus, CheckCircle, Circle, User } from 'lucide-react';
import { subtaskService } from '../../services/subtaskService';
import SubtaskCreateModal from './SubtaskCreateModal';
import toast from 'react-hot-toast';

interface Subtask {
  task_id: string;
  title: string;
  description?: string;
  status: 'pending' | 'in_progress' | 'completed';
  assigned_to?: string;
  assigned_to_name?: string;
  parent_task_id: string;
  created_at: string;
}

interface SubtaskListProps {
  parentTaskId: string;
  onSubtaskUpdate?: () => void;
}

const SubtaskList: React.FC<SubtaskListProps> = ({ parentTaskId, onSubtaskUpdate }) => {
  const [subtasks, setSubtasks] = useState<Subtask[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Load subtasks
  const loadSubtasks = async () => {
    if (!parentTaskId) return;

    setLoading(true);
    try {
      const fetchedSubtasks = await subtaskService.getSubtasks(parentTaskId);
      setSubtasks(fetchedSubtasks);
    } catch (error) {
      console.error('[SubtaskList] Failed to load subtasks:', error);
      toast.error('Failed to load subtasks');
    } finally {
      setLoading(false);
    }
  };

  // Load subtasks when expanded
  useEffect(() => {
    if (isExpanded && subtasks.length === 0) {
      loadSubtasks();
    }
  }, [isExpanded, parentTaskId]);

  // Calculate progress
  const completedCount = subtasks.filter(st => st.status === 'completed').length;
  const totalCount = subtasks.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  // Handle subtask completion toggle
  const handleToggleComplete = async (subtask: Subtask) => {
    const newStatus = subtask.status === 'completed' ? 'pending' : 'completed';

    // Optimistic update
    setSubtasks(prev =>
      prev.map(st =>
        st.task_id === subtask.task_id ? { ...st, status: newStatus } : st
      )
    );

    try {
      await subtaskService.updateSubtask(subtask.task_id, { status: newStatus });
      toast.success(
        newStatus === 'completed' ? 'Subtask completed!' : 'Subtask reopened'
      );
      onSubtaskUpdate?.();
    } catch (error) {
      console.error('[SubtaskList] Failed to update subtask:', error);
      // Rollback on error
      setSubtasks(prev =>
        prev.map(st =>
          st.task_id === subtask.task_id ? { ...st, status: subtask.status } : st
        )
      );
      toast.error('Failed to update subtask');
    }
  };

  // Handle subtask created
  const handleSubtaskCreated = (newSubtask: Subtask) => {
    setSubtasks(prev => [...prev, newSubtask]);
    setShowCreateModal(false);
    toast.success('Subtask created!');
    onSubtaskUpdate?.();
  };

  if (totalCount === 0 && !isExpanded) {
    return null; // Don't show anything if no subtasks
  }

  return (
    <div className="mt-4">
      {/* Header with expand/collapse toggle */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          )}
          <span className="font-medium text-gray-700 dark:text-gray-300">
            Subtasks {totalCount > 0 && `(${completedCount}/${totalCount})`}
          </span>
        </div>

        {/* Progress bar */}
        {totalCount > 0 && (
          <div className="flex items-center gap-3 flex-1 max-w-xs ml-4">
            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.3 }}
                className="bg-green-500 h-2 rounded-full"
              />
            </div>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {Math.round(progressPercentage)}%
            </span>
          </div>
        )}
      </button>

      {/* Expanded subtask list */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pl-8 mt-3 space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-honey-500"></div>
                </div>
              ) : subtasks.length === 0 ? (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No subtasks yet
                </div>
              ) : (
                subtasks.map((subtask, index) => (
                  <motion.div
                    key={subtask.task_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-honey-300 dark:hover:border-honey-600 transition-colors"
                  >
                    {/* Checkbox to complete */}
                    <button
                      onClick={() => handleToggleComplete(subtask)}
                      className="flex-shrink-0 transition-transform hover:scale-110"
                    >
                      {subtask.status === 'completed' ? (
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      ) : (
                        <Circle className="w-6 h-6 text-gray-400 hover:text-green-500" />
                      )}
                    </button>

                    {/* Subtask details */}
                    <div className="flex-1 min-w-0">
                      <h4
                        className={`font-medium text-gray-900 dark:text-white ${
                          subtask.status === 'completed'
                            ? 'line-through text-gray-500 dark:text-gray-400'
                            : ''
                        }`}
                      >
                        {subtask.title}
                      </h4>
                      {subtask.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {subtask.description}
                        </p>
                      )}
                    </div>

                    {/* Assigned person */}
                    {subtask.assigned_to_name && (
                      <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-900/20 rounded-full">
                        <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                          {subtask.assigned_to_name}
                        </span>
                      </div>
                    )}
                  </motion.div>
                ))
              )}

              {/* Add Subtask button */}
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center justify-center gap-2 w-full p-3 bg-honey-50 dark:bg-honey-900/20 border-2 border-dashed border-honey-300 dark:border-honey-600 rounded-lg text-honey-700 dark:text-honey-400 hover:bg-honey-100 dark:hover:bg-honey-900/30 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium">Add Subtask</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create subtask modal */}
      <SubtaskCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        parentTaskId={parentTaskId}
        onSubtaskCreated={handleSubtaskCreated}
      />
    </div>
  );
};

export default SubtaskList;
